import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { GetProfilePayload } from '~~/layers/base/server/api/profile/index.get'
import type { UpdateProfileBody } from '~~/layers/base/services/profile.service'
import { useProfileService } from '~~/layers/base/services/profile.service'
import type { ContactAddress } from '~~/layers/base/stores/contact.store'
import { useReturnAddressStore } from '~~/layers/base/stores/returnAddress.store'
import {
    formatProfileDisplayName,
    type ProfileNameFields,
} from '~~/layers/base/utils/mapAddressLikeToCheckoutCartAddress'
import {
    reportMissingProfileNameFields,
    resetReportedMissingProfileNameFields,
} from '~~/layers/base/utils/profile/reportMissingProfileNameFields'
import { useDatadog } from '~~/layers/base/composables/useDatadog'

type FetchStatus = 'idle' | 'loading' | 'ready' | 'error'

/** sessionStorage — survives Capacitor/WebView refresh; logout `clearProfile` removes it. */
const PROFILE_ROW_STORAGE_KEY = 'crane.profile.row.v1'

/** sessionStorage — survives Capacitor/WebView refresh; logout `clearAddresses` removes it. */
const PROFILE_ADDRESSES_STORAGE_KEY = 'crane.profile.addresses.v1'

function readProfileCache(): GetProfilePayload | null {
    if (!import.meta.client) {
        return null
    }
    try {
        const raw = sessionStorage.getItem(PROFILE_ROW_STORAGE_KEY)
        if (raw == null) {
            return null
        }
        const parsed: unknown = JSON.parse(raw)
        if (!parsed || typeof parsed !== 'object') {
            return null
        }
        const row = parsed as GetProfilePayload
        return typeof row.id === 'string' ? row : null
    } catch {
        return null
    }
}

function writeProfileCache(row: GetProfilePayload): void {
    if (!import.meta.client) {
        return
    }
    try {
        sessionStorage.setItem(PROFILE_ROW_STORAGE_KEY, JSON.stringify(row))
    } catch {
        /* quota / private mode */
    }
}

function clearProfileCache(): void {
    if (!import.meta.client) {
        return
    }
    try {
        sessionStorage.removeItem(PROFILE_ROW_STORAGE_KEY)
    } catch {
        /* best-effort */
    }
}

function readAddressesCache(): ContactAddress[] | null {
    if (!import.meta.client) {
        return null
    }
    try {
        const raw = sessionStorage.getItem(PROFILE_ADDRESSES_STORAGE_KEY)
        if (raw == null) {
            return null
        }
        const parsed: unknown = JSON.parse(raw)
        return Array.isArray(parsed) ? (parsed as ContactAddress[]) : null
    } catch {
        return null
    }
}

function writeAddressesCache(rows: ContactAddress[]): void {
    if (!import.meta.client) {
        return
    }
    try {
        sessionStorage.setItem(PROFILE_ADDRESSES_STORAGE_KEY, JSON.stringify(rows))
    } catch {
        /* quota / private mode */
    }
}

function clearAddressesCache(): void {
    if (!import.meta.client) {
        return
    }
    try {
        sessionStorage.removeItem(PROFILE_ADDRESSES_STORAGE_KEY)
    } catch {
        /* best-effort */
    }
}

/**
 * Client-side cache for `GET /api/profile` (signed-in `public.profile` row today; may hold more
 * profile-shaped fields later). Synced with Better Auth via `plugins/profile-auth-sync.client.ts`.
 * Route middleware on the client uses `ensureProfileLoaded()` so guards and nav share one fetch.
 * Profile row hydrates from sessionStorage on refresh; `ensureProfileLoaded` revalidates in the
 * background when a cached row is already ready (same SWR pattern as profile addresses).
 */
export const useProfileStore = defineStore('profile', () => {
    const {
        doFetchProfile,
        doUpdateProfile,
        doRequestAccountDeletion,
        doFetchProfileAddresses,
        doPatchProfileAddress,
    } = useProfileService()

    const datadog = useDatadog()
    const cachedProfile = readProfileCache()
    const profile = ref<GetProfilePayload | null>(cachedProfile)
    const status = ref<FetchStatus>(cachedProfile != null ? 'ready' : 'idle')

    /** Profile-owned return addresses from `GET /api/profile/addresses` (Manage addresses page). */
    const cachedAddresses = readAddressesCache()
    const addresses = ref<ContactAddress[]>(cachedAddresses ?? [])
    const addressesLoading = ref(false)
    const addressesStatus = ref<FetchStatus>(cachedAddresses != null ? 'ready' : 'idle')

    /** Coalesces concurrent `fetchProfile` calls (middleware + plugin + top nav). */
    let fetchProfileInFlight: Promise<void> | null = null

    /** Coalesces concurrent `fetchAddresses` calls (e.g. profile home + manage-addresses page). */
    let fetchAddressesInFlight: Promise<ContactAddress[]> | null = null

    /**
     * In-flight make-default id. A later tap replaces it; a stale PATCH revert must not
     * stomp a newer optimistic pick, and a racing GET must re-apply this flag.
     */
    let pendingDefaultId: string | null = null

    function applyDefaultFlags(id: string | null): void {
        if (!id) {
            return
        }
        addresses.value = addresses.value.map((row) => ({
            ...row,
            is_default: row.id === id,
        }))
        writeAddressesCache(addresses.value)
    }

    /** True when the cached profile has staff or admin flags (non-anonymous session only). */
    const isStaffOrAdmin = computed(() => {
        const p = profile.value
        return Boolean(p?.isStaff || p?.isAdmin)
    })

    /** Joined profile first / last for display; null when both columns are empty. */
    const profileDisplayName = computed(() => formatProfileDisplayName(profile.value))

    /** Profile name columns for return-address injection (checkout / envelope). */
    const profileNameFields = computed((): ProfileNameFields | null => {
        if (!profile.value) {
            return null
        }
        return {
            firstName: profile.value.firstName,
            lastName: profile.value.lastName,
        }
    })

    /**
     * Drop Manage-addresses cache so `ensureAddressesLoaded` cannot short-circuit on a prior
     * account's `addressesStatus === 'ready'` after logout / account switch.
     */
    function clearAddresses(): void {
        pendingDefaultId = null
        addresses.value = []
        addressesStatus.value = 'idle'
        addressesLoading.value = false
        fetchAddressesInFlight = null
        clearAddressesCache()
    }

    function clearProfile(): void {
        profile.value = null
        status.value = 'idle'
        resetReportedMissingProfileNameFields()
        clearProfileCache()
        clearAddresses()
    }

    function applyLoadedProfile(row: GetProfilePayload | null): void {
        profile.value = row
        if (row) {
            writeProfileCache(row)
            reportMissingProfileNameFields(row, useDatadog())
        }
    }

    /**
     * Loads `GET /api/profile` and updates `profile`.
     * While Better Auth session is still pending, applies the API payload so route guards can read
     * roles; once resolved, clears if the user is not a non-anonymous session (logout / anonymous).
     */
    async function fetchProfile(): Promise<void> {
        if (fetchProfileInFlight) {
            await fetchProfileInFlight
            return
        }

        fetchProfileInFlight = (async () => {
            const { session, isLoggedIn } = useBetterAuth()
            const keepShowingCache = status.value === 'ready' && profile.value != null

            if (!keepShowingCache) {
                status.value = 'loading'
            }
            const { data, error: err } = await doFetchProfile()

            if (err.value) {
                if (!keepShowingCache) {
                    profile.value = null
                    status.value = 'error'
                }
                return
            }

            const isPending = Boolean(session.value?.isPending)

            if (isPending) {
                applyLoadedProfile(data.value?.profile ?? null)
                status.value = 'ready'
                return
            }

            if (!isLoggedIn.value) {
                profile.value = null
                status.value = 'idle'
                clearProfileCache()
                return
            }

            applyLoadedProfile(data.value?.profile ?? null)
            status.value = 'ready'
        })()

        try {
            await fetchProfileInFlight
        } finally {
            fetchProfileInFlight = null
        }
    }

    /**
     * Resolves profile for client UI and middleware: returns cached row when ready, then
     * revalidates in the background without a loading skeleton. Hydrated from sessionStorage
     * after refresh. Logout `clearProfile` drops this cache.
     */
    async function ensureProfileLoaded(): Promise<GetProfilePayload | null> {
        if (status.value === 'ready') {
            void fetchProfile()
            return profile.value
        }
        await fetchProfile()
        return profile.value
    }

    /**
     * Updates the signed-in user's `first_name` / `last_name` / `phone_number` via
     * `PATCH /api/profile`. The endpoint returns the full row (same shape as GET), so the
     * response is applied directly — no extra re-fetch round-trip.
     */
    async function updateProfile(body: UpdateProfileBody): Promise<GetProfilePayload | null> {
        const { data, error: err } = await doUpdateProfile(body)
        if (err.value) {
            throw err.value
        }
        profile.value = data.value?.profile ?? null
        status.value = 'ready'
        if (profile.value) {
            writeProfileCache(profile.value)
            reportMissingProfileNameFields(profile.value, useDatadog())
        }
        return profile.value
    }

    /**
     * Requests account soft-deletion (doc/spec/SPEC_delete_profile.md — `POST
     * /api/profile/delete-account`). Returns the raw `{ queued }` response; callers are
     * responsible for signing the user out afterward (the backend already revokes all Better
     * Auth sessions server-side, so client-side logout is just local cleanup + navigation).
     */
    async function requestAccountDeletion(): Promise<{ queued: boolean }> {
        const { data, error: err } = await doRequestAccountDeletion()
        if (err.value) {
            throw err.value
        }
        return data.value!
    }

    /**
     * Loads `GET /api/profile/addresses` and sets `addresses`. Always hits the network — call
     * after add/edit/delete so the list stays in sync. Concurrent calls are coalesced.
     * For an initial page load, prefer `ensureAddressesLoaded()`. Make-default uses
     * `setDefaultAddress` (optimistic cache, no loading skeleton).
     */
    async function fetchAddresses(): Promise<ContactAddress[]> {
        if (fetchAddressesInFlight) {
            return fetchAddressesInFlight
        }

        fetchAddressesInFlight = (async () => {
            const keepShowingCache = addressesStatus.value === 'ready'
            if (!keepShowingCache) {
                addressesLoading.value = true
                addressesStatus.value = 'loading'
            }
            try {
                const { data, error: err } = await doFetchProfileAddresses()
                if (err.value) {
                    if (!keepShowingCache) {
                        addressesStatus.value = 'error'
                    }
                    throw err.value
                }
                addresses.value = data.value?.addresses ?? []
                applyDefaultFlags(pendingDefaultId)
                addressesStatus.value = 'ready'
                writeAddressesCache(addresses.value)
                return addresses.value
            } finally {
                addressesLoading.value = false
            }
        })()

        try {
            return await fetchAddressesInFlight
        } finally {
            fetchAddressesInFlight = null
        }
    }

    /**
     * Resolves addresses for client UI: returns the cached list when ready, then
     * revalidates in the background without a skeleton. Warmed at sign-in via
     * `profile-auth-sync` and hydrated from sessionStorage after refresh. Logout
     * `clearProfile` drops this cache.
     */
    async function ensureAddressesLoaded(): Promise<ContactAddress[]> {
        if (addressesStatus.value === 'ready') {
            // Background revalidate must not reject into an unhandled promise (offline cold start),
            // but failures should still reach Datadog.
            void fetchAddresses().catch((error: unknown) => {
                const err = error instanceof Error ? error : new Error(String(error))
                datadog.logError(err, {
                    feature: 'profile',
                    action: 'fetchAddresses',
                    phase: 'background-revalidate',
                })
            })
            return addresses.value
        }
        return fetchAddresses()
    }

    /**
     * Optimistic make-default (no list skeleton). Last tap wins: `pendingDefaultId`
     * ignores a stale PATCH revert and re-applies the flag after a racing GET.
     * Mirrors into `useReturnAddressStore` so Send can show the new default
     * before PATCH returns (separate cache from Account).
     */
    async function setDefaultAddress(id: string): Promise<void> {
        if (addresses.value.find((row) => row.id === id)?.is_default) {
            return
        }
        const previousDefaultId = addresses.value.find((row) => row.is_default)?.id ?? null
        pendingDefaultId = id
        applyDefaultFlags(id)
        const returnAddressStore = useReturnAddressStore()
        returnAddressStore.applyOptimisticDefault(id)
        const { error: err } = await doPatchProfileAddress(id, { is_default: true })
        if (!err.value) {
            if (pendingDefaultId === id) {
                pendingDefaultId = null
                returnAddressStore.commitOptimisticDefault()
            }
            return
        }
        if (pendingDefaultId !== id) {
            return
        }
        pendingDefaultId = null
        if (previousDefaultId) {
            applyDefaultFlags(previousDefaultId)
        } else {
            addresses.value = addresses.value.map((row) => ({
                ...row,
                is_default: false,
            }))
            writeAddressesCache(addresses.value)
        }
        returnAddressStore.revertOptimisticDefault(previousDefaultId)
        throw err.value
    }

    return {
        profile,
        status,
        isStaffOrAdmin,
        profileDisplayName,
        profileNameFields,
        fetchProfile,
        clearProfile,
        ensureProfileLoaded,
        updateProfile,
        requestAccountDeletion,
        addresses,
        addressesLoading,
        addressesStatus,
        clearAddresses,
        fetchAddresses,
        ensureAddressesLoaded,
        setDefaultAddress,
    }
})
